import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, Key, Loader2, Trash2, Zap } from 'lucide-react';
import FieldTooltip from '@/components/ui/FieldTooltip';

const GROUP_ORDER = ['whatsapp', 'meta', 'tiktok', 'voz', 'correo', 'calendarios', 'ia', 'pagos', 'seguridad', 'otras'];
const GROUP_TITLES = {
  whatsapp: 'WhatsApp',
  meta: 'Meta (Facebook / Instagram)',
  tiktok: 'TikTok',
  voz: 'Voz y teléfono',
  correo: 'Correo',
  calendarios: 'Calendarios',
  ia: 'IA',
  pagos: 'Pagos',
  seguridad: 'Seguridad y archivos',
  otras: 'Otras',
};

function CardInteg({ k, integ, onConfigure, onRemove, onTest, testing }) {
  if (!integ) return null;
  return (
    <div
      className={`rounded-xl border p-4 transition-all ${integ.configured ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/5 bg-black/20'}`}
      data-testid={`platform-integ-${k}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <p className="text-sm font-medium text-white flex items-center gap-1">
            {integ.name}
            {integ.ayuda && <FieldTooltip tip={integ.ayuda} size={12} />}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{integ.description}</p>
          {integ.si_falla && (
            <p className="text-[9px] text-amber-400/70 mt-1">Si falla: {integ.si_falla}</p>
          )}
        </div>
        {integ.configured ? (
          <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shrink-0">
            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Activo
          </Badge>
        ) : (
          <Badge className="text-[8px] bg-white/5 text-muted-foreground border-white/10 shrink-0">Sin configurar</Badge>
        )}
      </div>
      {integ.donde && <p className="text-[9px] text-muted-foreground mb-2">Dónde: {integ.donde}</p>}
      {integ.configured && integ.updated_at && (
        <p className="text-[9px] text-muted-foreground mb-2">
          <Clock className="w-2.5 h-2.5 inline mr-0.5" />
          {new Date(integ.updated_at).toLocaleDateString('es-MX')}
        </p>
      )}
      <div className="flex gap-1 mt-3">
        <Button
          variant="outline" size="sm"
          onClick={() => onConfigure(k)}
          className="flex-1 text-[10px] h-7 border-white/10"
          data-testid={`configure-integ-${k}`}
        >
          <Key className="w-3 h-3 mr-0.5" /> {integ.configured ? 'Editar' : 'Configurar'}
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => onTest(k)}
          disabled={!!testing}
          className="text-[10px] h-7 border-white/10"
          data-testid={`test-integ-${k}`}
        >
          {testing === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
        </Button>
        {integ.configured && (
          <Button variant="outline" size="sm" onClick={() => onRemove(k)}
            className="text-[10px] h-7 border-red-500/20 text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsBoard({
  items, onConfigure, onRemove, onTest, testing, sesHref,
}) {
  const byGroup = {};
  Object.entries(items || {}).forEach(([k, integ]) => {
    const g = integ?.grupo || 'otras';
    (byGroup[g] ||= []).push([k, integ]);
  });
  const order = GROUP_ORDER.filter((g) => g === 'correo' || (byGroup[g] && byGroup[g].length));

  return (
    <div className="space-y-6" data-testid="integrations-board">
      {order.map((gid) => (
        <div key={gid}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
            {GROUP_TITLES[gid] || gid}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {gid === 'correo' && (
              <div className="rounded-xl border border-white/5 bg-black/20 p-4" data-testid="platform-integ-aws_ses">
                <p className="text-sm font-medium text-white">AWS SES (no disponible)</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Alternativa futura para correo. Hoy NO está activo; todo el correo sale por SendGrid.
                </p>
                <p className="text-[9px] text-muted-foreground mt-1">Inactivo hoy. El correo sale por SendGrid.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 text-[10px] h-7 border-white/10"
                  onClick={() => { window.location.assign(sesHref || '/backoffice/email-config'); }}
                  data-testid="open-email-ses"
                >
                  Configurar correo (SendGrid)
                </Button>
              </div>
            )}
            {(byGroup[gid] || []).map(([k, integ]) => (
              <CardInteg
                key={k}
                k={k}
                integ={integ}
                onConfigure={onConfigure}
                onRemove={onRemove}
                onTest={onTest}
                testing={testing}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
